import React, { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Card,
  Typography,
  Flex,
  Select,
  Input,
  InputNumber,
  Button,
  Empty,
  Switch,
  Tag,
  message,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import {
  type Bid,
  type BidCondition,
  type BidConditionField,
  type BidConditionComparator,
  type BidMetrics,
  bidConditionFieldOptions,
  getComparatorsForField,
  validateConditionValue,
  nextBiddingId,
} from '@/data'
import { deleteBidRung } from '@/api/lop/bid-rungs/deleteBidRung'
import {
  syncBidRungs,
  type BidRungSyncItem,
} from '@/api/lop/bid-rungs/syncBidRungs'
import { updateBidRung } from '@/api/lop/bid-rungs/updateBidRung'
import type { BidRungPayload } from '@/api/lop/bid-rungs/createBidRung'
import type { BiddingEditorContext } from '../../index'

const rungConditions = (bid: Bid) =>
  bid.conditions.map(({ field, operator, value }) => ({
    field,
    operator,
    value,
  }))

const buildPayload = (
  bid: Bid,
  partner_id: number,
  priority: number,
  overrides?: Partial<BidRungPayload>,
): BidRungPayload => ({
  partner_id,
  name: bid.name,
  bid_amount: bid.bid_amount,
  priority,
  active: bid.active,
  conditions: rungConditions(bid),
  ...overrides,
})

const buildSyncItem = (bid: Bid, priority: number): BidRungSyncItem => ({
  id: bid.id,
  name: bid.name,
  bid_amount: bid.bid_amount,
  priority,
  active: bid.active,
  conditions: rungConditions(bid),
})

const { Title, Text } = Typography

const formatPercent = (v: string | null | undefined) =>
  v != null ? `${v}%` : '-'

const BidMetricsLabel: React.FC<{ metrics: BidMetrics }> = ({ metrics }) => (
  <span style={{ fontSize: 12, marginLeft: 6, color: '#8c8c8c' }}>
    (Leads:{' '}
    {metrics.lead_count != null ? metrics.lead_count.toLocaleString() : '-'},
    CPA: {formatPercent(metrics.cpa)}, CVR: {formatPercent(metrics.cvr)}, LTC:{' '}
    {formatPercent(metrics.ltc)})
  </span>
)

const reorder = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list
  const next = [...list]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

const BidConditionRow: React.FC<{
  condition: BidCondition
  onChange: (updates: Partial<BidCondition>) => void
  onRemove: () => void
}> = ({ condition, onChange, onRemove }) => {
  const allowedComparators = getComparatorsForField(condition.field)
  const valueError = validateConditionValue(condition.field, condition.value)

  const handleFieldChange = (field: BidConditionField) => {
    const nextAllowed = getComparatorsForField(field)
    const keepOperator = nextAllowed.some((o) => o.value === condition.operator)
    onChange({
      field,
      operator: keepOperator ? condition.operator : '==',
      value: '',
    })
  }

  return (
    <Flex gap="small" wrap="wrap" align="start">
      <Flex vertical gap={4} style={{ minWidth: 360 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Field
        </Text>
        <Select<BidConditionField>
          value={condition.field}
          onChange={handleFieldChange}
          options={[...bidConditionFieldOptions]}
          style={{ width: 360 }}
        />
      </Flex>

      <Flex vertical gap={4} style={{ minWidth: 140 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Comparator
        </Text>
        <Select<BidConditionComparator>
          value={condition.operator}
          onChange={(value) => onChange({ operator: value, value: '' })}
          options={[...allowedComparators]}
          style={{ width: 140 }}
        />
      </Flex>

      <Flex vertical gap={4} style={{ flex: 1, minWidth: 200 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Value
        </Text>
        <Input
          value={condition.value}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="value"
          status={valueError ? 'error' : undefined}
        />
        {valueError && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {valueError}
          </Text>
        )}
      </Flex>

      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={onRemove}
        style={{ marginTop: 22 }}
      />
    </Flex>
  )
}

const BidCard: React.FC<{
  bid: Bid
  index: number
  total: number
  busy: boolean
  onChange: (updates: Partial<Bid>) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onToggleEnabled: (value: boolean) => void
}> = ({
  bid,
  index,
  total,
  busy,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onToggleEnabled,
}) => {
  const isDraft = bid.id <= 0
  const addCondition = () => {
    const newCondition: BidCondition = {
      id: nextBiddingId(),
      field: 'annual_income',
      operator: '>=',
      value: '',
    }
    onChange({ conditions: [...bid.conditions, newCondition] })
  }

  const updateCondition = (id: number, updates: Partial<BidCondition>) => {
    onChange({
      conditions: bid.conditions.map((c) =>
        c.id === id ? { ...c, ...updates } : c,
      ),
    })
  }

  const removeCondition = (id: number) => {
    onChange({ conditions: bid.conditions.filter((c) => c.id !== id) })
  }

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap="small">
          <Tag color="blue">#{index + 1}</Tag>
          <Text strong>{bid.name || 'Untitled bid'}</Text>
          {bid.metrics && <BidMetricsLabel metrics={bid.metrics} />}
          {isDraft && <Tag color="gold">Unsaved</Tag>}
          {!bid.active && <Tag color="default">Disabled</Tag>}
        </Flex>
      }
      extra={
        <Flex gap={4}>
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || busy || isDraft}
            onClick={onMoveUp}
          />
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            disabled={index === total - 1 || busy || isDraft}
            onClick={onMoveDown}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Flex>
      }
    >
      <Flex vertical gap="middle">
        <Flex gap="middle" wrap="wrap" align="end">
          <Flex vertical gap={4} style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Bid name
            </Text>
            <Input
              value={bid.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="e.g. High-income / Good credit"
            />
          </Flex>
          <Flex vertical gap={4} style={{ minWidth: 140 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Bid amount ($)
            </Text>
            <InputNumber
              min={0}
              step={0.5}
              value={bid.bid_amount}
              onChange={(v) => onChange({ bid_amount: v ?? 0 })}
              style={{ width: 140 }}
            />
          </Flex>
          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Enabled
            </Text>
            <Switch
              checked={bid.active}
              onChange={(v) => onToggleEnabled(v)}
              disabled={busy}
            />
          </Flex>
        </Flex>

        <div>
          <Flex justify="space-between" align="center">
            <Text strong>Conditions</Text>
            <Button
              type="link"
              icon={<PlusOutlined />}
              size="small"
              onClick={addCondition}
            >
              Add condition
            </Button>
          </Flex>
          {bid.conditions.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No conditions — this bid will match every lead.
            </Text>
          ) : (
            <Flex vertical gap="small" style={{ marginTop: 8 }}>
              {bid.conditions.map((c) => (
                <BidConditionRow
                  key={c.id}
                  condition={c}
                  onChange={(updates) => updateCondition(c.id, updates)}
                  onRemove={() => removeCondition(c.id)}
                />
              ))}
            </Flex>
          )}
        </div>
      </Flex>
    </Card>
  )
}

export const BidsPage: React.FC = () => {
  const { bids, setBids, partnerId } = useOutletContext<BiddingEditorContext>()
  const [busy, setBusy] = useState(false)

  const addBid = () => {
    const draft: Bid = {
      id: nextBiddingId(),
      partner_id: partnerId,
      name: '',
      bid_amount: 0,
      conditions: [],
      priority: bids.length + 1,
      active: true,
    }
    setBids([...bids, draft])
  }

  const updateBid = (id: number, updates: Partial<Bid>) => {
    setBids(bids.map((b) => (b.id === id ? { ...b, ...updates } : b)))
  }

  const removeBid = async (id: number) => {
    if (id <= 0) {
      setBids(bids.filter((b) => b.id !== id))
      return
    }
    try {
      setBusy(true)
      await deleteBidRung(id)
      setBids(bids.filter((b) => b.id !== id))
    } catch (err) {
      message.error('Failed to remove bid')
      console.error(err)
    } finally {
      setBusy(false)
    }
  }

  const toggleEnabled = async (bid: Bid, value: boolean) => {
    const optimistic = bids.map((b) =>
      b.id === bid.id ? { ...b, active: value } : b,
    )
    setBids(optimistic)
    if (bid.id <= 0) return
    try {
      setBusy(true)
      const rung = await updateBidRung(
        bid.id,
        buildPayload(bid, partnerId, bid.priority, { active: value }),
      )
      setBids(optimistic.map((b) => (b.id === bid.id ? rung : b)))
    } catch (err) {
      message.error('Failed to update bid')
      console.error(err)
      setBids(bids)
    } finally {
      setBusy(false)
    }
  }

  const moveBid = async (index: number, delta: number) => {
    const reordered = reorder(bids, index, index + delta)
    if (reordered === bids) return
    const persisted = reordered.filter((b) => b.id > 0)
    if (persisted.length < 2) {
      setBids(reordered)
      return
    }
    setBids(reordered)
    try {
      setBusy(true)
      const rungs = await syncBidRungs({
        partner_id: partnerId,
        rungs: persisted.map((b, i) => buildSyncItem(b, i + 1)),
      })
      const byId = new Map(rungs.map((r) => [r.id, r]))
      setBids(reordered.map((b) => byId.get(b.id) ?? b))
    } catch (err) {
      message.error('Failed to reorder bids')
      console.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Bids</Title>
          <Text type="secondary">
            Bids are evaluated top-to-bottom against each inbound lead. The
            first bid whose conditions all match wins. Use the arrows to
            reorder.
          </Text>
        </div>

        <Card
          title="Bid Ladder"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addBid}
            >
              Add Bid
            </Button>
          }
        >
          {bids.length === 0 ? (
            <Empty
              description={
                <Text type="secondary">
                  No bids yet. Add a bid to get started.
                </Text>
              }
            >
              <Button type="dashed" icon={<PlusOutlined />} onClick={addBid}>
                Add Bid
              </Button>
            </Empty>
          ) : (
            <Flex vertical gap="middle">
              {bids.map((bid, index) => (
                <BidCard
                  key={bid.id}
                  bid={bid}
                  index={index}
                  total={bids.length}
                  busy={busy}
                  onChange={(updates) => updateBid(bid.id, updates)}
                  onRemove={() => removeBid(bid.id)}
                  onMoveUp={() => moveBid(index, -1)}
                  onMoveDown={() => moveBid(index, 1)}
                  onToggleEnabled={(v) => toggleEnabled(bid, v)}
                />
              ))}
            </Flex>
          )}
        </Card>
      </Flex>
    </div>
  )
}
